import React, { useState } from 'react'
import { User } from 'lucide-react'
import { Badge, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, CopyableId, CopyableText, cn } from '@duck/ui'
import { UserAvatar } from '@/components/UserAvatar'
import { useTar } from '@/modules'
import { useUserDetails } from '@/hooks/useUserDetails'
import { useAuth } from '@/hooks/useAuth'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { formatRole, getRoleBadgeColor } from '@/components/AdminUsers/userAdmin.utils'
import { UserService } from '@duck/api-definitions'

export interface UserBadgeProps {
  userId: string
  firstName?: string
  lastName?: string
  fullName?: string
  role?: string
  avatar?: string
  className?: string
  /** Fallback email shown in the tooltip when the user record isn't (or can't be) fetched —
   *  e.g. a partner viewing an invited person who hasn't joined yet (no userId). */
  email?: string
  /** Suppress the hover/focus tooltip entirely — use when the badge sits inside a clickable
   *  row whose own click target (e.g. a results dialog) would otherwise compete with it. */
  disableTooltip?: boolean
}

export const UserBadge: React.FC<UserBadgeProps> = ({
  userId,
  firstName,
  lastName,
  fullName,
  role,
  avatar,
  className,
  email,
  disableTooltip,
}) => {
  const { t } = useTar()
  const { isPartner } = useAuth()
  const [open, setOpen] = useState(false)

  // The user-details endpoint is now partner-accessible (server scopes activity to the partner's
  // org). Partner mode only differs in presentation: non-clickable (no admin user page) and we
  // hide admin-only fields (internal id, platform role).
  const partnerMode = isPartner

  const { user, challenges, assignments, isLoading } = useUserDetails(userId, {
    enabled: open && !!userId,
  })

  const label =
    firstName || lastName
      ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
      : (fullName ?? userId)

  const openPage = () =>
    window.open(`/admin/users/${encodeURIComponent(userId)}`, '_blank', 'noopener,noreferrer')

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPage()
    }
  }

  const clickable = !partnerMode
  const triggerProps = clickable
    ? { role: 'button' as const, tabIndex: 0, onClick: openPage, onKeyDown: handleKeyDown }
    : {}

  const tipFirst = user?.firstName ?? firstName
  const tipLast = user?.lastName ?? lastName
  const tipRole = user?.role ?? role
  const tipEmail = user?.email ?? email

  const badge = (
    // span (not button) avoids nesting a <div> (Badge) inside a <button>
    <span {...triggerProps} className={className}>
      <Badge
        variant="outline"
        className={cn(
          'gap-1.5 border-primary/30 bg-primary/10 text-foreground max-w-full',
          clickable ? 'cursor-pointer hover:bg-primary/20' : 'cursor-default'
        )}
      >
        <User className="w-3.5 h-3.5 shrink-0 text-primary" />
        <span className="truncate">{label}</span>
      </Badge>
    </span>
  )

  if (disableTooltip) return badge

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="w-72 p-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <UserAvatar
                size="md"
                user={{
                  avatar: user?.avatar ?? avatar,
                  firstName: tipFirst,
                  lastName: tipLast,
                }}
              />
              <div className="min-w-0">
                <p className="font-semibold text-foreground truncate">
                  {`${tipFirst ?? ''} ${tipLast ?? ''}`.trim() || label}
                </p>
                {!partnerMode && tipRole && (
                  <Badge className={getRoleBadgeColor(tipRole as UserService.UserRole)}>
                    {formatRole(tipRole)}
                  </Badge>
                )}
              </div>
            </div>
            {isLoading ? (
              <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
            ) : (
              <div className="space-y-1 text-xs">
                {!partnerMode && user && (
                  <div className="flex items-center gap-1" dir="ltr">
                    <span className="text-muted-foreground">{t('common.id')}:</span>
                    <CopyableId value={user.id} />
                  </div>
                )}
                {tipEmail && (
                  <div className="flex items-center gap-1" dir="ltr">
                    <span className="text-muted-foreground">{t('common.email')}:</span>
                    <CopyableText value={tipEmail}>
                      <span className="font-mono break-all">{tipEmail}</span>
                    </CopyableText>
                  </div>
                )}
                {user?.createdAt && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">{t('users.joined')}:</span>
                    <RelativeDateTime isoDateString={user.createdAt} className="text-foreground" nativeTooltip />
                  </div>
                )}
                {user && (
                  partnerMode ? (
                    <p className="text-muted-foreground">
                      {t('partner.assignmentsCount', { count: assignments?.length ?? 0 })}
                    </p>
                  ) : (
                    <p className="text-muted-foreground">
                      {t('users.badgeActivitySummary', {
                        challenges: challenges?.length ?? 0,
                        assignments: assignments?.length ?? 0,
                      })}
                    </p>
                  )
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
